import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import {
    FlyFreelyLoggingService,
    PersonsOrganisationDto,
    UserService,
    hasAnyPermission
} from '@flyfreely-portal-ui/flyfreely';
import {
    LoadingOrganisations,
    SelectedOrganisation,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

/**
 * This guard checks that the current user has permission to view the organisation specified
 * by the `organisationId` query parameter.
 *
 * You often need to add `runGuardsAndResolvers: 'always'` to the router configuration so the change of
 * organisation is detected.
 *
 * The guard triggers org loading itself because WorkspaceComponent processes query params
 * only after navigation succeeds (after guards run). This ensures the async org fetch happens
 * before the permission check.
 *
 * If the user has no access to the organisation, they are redirected to `/`.
 * If the user lacks `ORGANISATION_VIEW` permission, they are redirected to `/missions` (roles
 * without view permission may still access mission lists).
 */
@Injectable()
export class AccountPermissionsGuard {
    constructor(
        private workspaceStateService: WorkspaceStateService,
        private userService: UserService,
        private logger: FlyFreelyLoggingService,
        private router: Router
    ) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        if (route.queryParams.organisationId == null) {
            this.logger.error('No current organisationId');
            return of(false);
        }

        const orgId = parseInt(route.queryParams.organisationId);

        // Roles with mission access but no organisation view (e.g. external student): redirect to
        // /missions and return BEFORE changeOrganisation() below. That call broadcasts the org to
        // still-mounted Manage widgets, which would then fetch data for an org the user can't view and
        // log "Operation forbidden" errors. Returning early avoids loading the org until after redirect.
        const knownOrg = this.userService
            .findUsersOrganisations()
            .find(o => o.id === orgId);

        if (
            knownOrg != null &&
            hasAnyPermission(
                knownOrg,
                PersonsOrganisationDto.Permissions.MISSION_VIEW
            ) &&
            !hasAnyPermission(
                knownOrg,
                PersonsOrganisationDto.Permissions.ORGANISATION_VIEW
            )
        ) {
            return of(
                this.router.createUrlTree(['/missions'], {
                    queryParams: route.queryParams
                })
            );
        }

        this.workspaceStateService.changeOrganisation(orgId);

        return this.workspaceStateService.currentOrganisation$.pipe(
            filter(
                (
                    org
                ): org is Exclude<SelectedOrganisation, LoadingOrganisations> =>
                    org.type !== 'loading'
            ),
            take(1),
            map(org => {
                if (
                    org.type === 'no_access' ||
                    org.type === 'no_organisation'
                ) {
                    return this.router.createUrlTree(['/']);
                }
                // For now this guard will only check if the user can view the organisation,
                // routing to the missions dashboard if not. This is because roles without view
                // permission can still view mission lists.
                return hasAnyPermission(
                    org.organisation,
                    PersonsOrganisationDto.Permissions.ORGANISATION_VIEW
                )
                    ? true
                    : this.router.createUrlTree(['/'], {
                          queryParams: route.queryParams
                      });
            })
        );
    }
}
